import {
  Button,
  Close,
  InlineMessage,
  Input,
  Loader,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Success,
} from '@fountain/fountain-ui-components';
import { Avatar, Chip, makeStyles, Typography } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { CancelablePromise } from 'api-clients/monolith';
import { ScoreCardEvaluatorsService } from 'api-clients/monolith/services/ScoreCardEvaluatorsService';
import React, { useEffect, useRef, useState, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useApiServiceMutation } from 'hooks/useApiServiceMutation';
import { useDebounce } from 'hooks/useDebounce';
import globalMessages from 'shared/global/messages';
import { trackAnalyticsEvent } from 'utils/analytics';
import { getInitials } from 'utils/stringutils';

import { AssignedEvaluators } from './InviteEvaluators/AssignedEvaluators';
import {
  EvaluatorCandidate,
  ScorecardEvaluator,
} from './InviteEvaluators/inviteEvaluators';
import { useEvaluators } from './InviteEvaluators/useEvaluators';
import {
  SEARCH_MIN_LENGTH,
  useEvaluatorSearch,
} from './InviteEvaluators/useEvaluatorSearch';
import { messages } from './messages';

// Long enough to read the confirmation, short enough that the modal gets out
// of the way on its own.
export const AUTO_CLOSE_DELAY = 1400;

type RemoveEvaluatorResponse = Awaited<
  ReturnType<
    typeof ScoreCardEvaluatorsService.deleteInternalApiScoreCardsTemplatesApplicantsEvaluators
  >
>;

const useStyles = makeStyles(theme => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2, 0),
  },
  option: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  optionIdentity: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  // One avatar treatment for a user everywhere the modal names one: the
  // search dropdown rows and the selected chips.
  userAvatar: {
    backgroundColor: theme.palette.common.blue400,
    color: theme.palette.common.white,
    fontSize: 12,
    height: 32,
    width: 32,
  },
  optionName: {
    color: theme.palette.text.primary,
  },
  // The theme's caption stays at 14px; the design sets the email a step
  // under the name.
  optionEmail: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  // Chip pins its avatar and delete icon through two-class selectors, so
  // the overrides nest here to outrank them. The chip carries the compact
  // 24px avatar; the dropdown rows keep the 32px one.
  chip: {
    backgroundColor: theme.palette.common.gray100,
    borderRadius: theme.misc.borderRadius,
    '& $userAvatar': {
      color: theme.palette.common.white,
      fontSize: 10,
      height: 24,
      width: 24,
    },
    // Chip sizes its delete icon with explicit dimensions, not font-size.
    '& .MuiChip-deleteIcon': {
      color: theme.palette.common.gray600,
      height: 11,
      width: 11,
    },
  },
  chipLabel: {
    color: theme.palette.text.primary,
  },
  success: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(3, 0),
    textAlign: 'center',
  },
  successIcon: {
    color: theme.palette.common.green600,
    fontSize: 32,
  },
  successTitle: {
    fontWeight: theme.typography.fontWeightBold as number,
  },
  successDetail: {
    color: theme.palette.text.secondary,
  },
}));

export interface InviteEvaluatorsModalProps {
  applicantId: string;
  scorecardTemplateId: string;
  scorecardName: string;
  handleClose: () => void;
}

// The invite evaluators flow for one scorecard on one applicant: the
// evaluators it already carries, a user search that starts at three
// characters, and the invite itself. Success replaces the form and the modal
// closes on its own.
export const InviteEvaluatorsModal: VFC<InviteEvaluatorsModalProps> = ({
  applicantId,
  scorecardTemplateId,
  scorecardName,
  handleClose,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const templateId = Number(scorecardTemplateId);

  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debounceSearchQuery = useDebounce(setSearchQuery, 500);
  const [selected, setSelected] = useState<EvaluatorCandidate[]>([]);
  const [sentCount, setSentCount] = useState<number | null>(null);

  const { result: evaluatorsResult, refetch: refetchEvaluators } =
    useEvaluators(templateId, applicantId);
  // A refetch moves the result to 'reloading' with the settled data still on
  // it; keeping the list up beats flashing the loader.
  const evaluatorsData =
    evaluatorsResult.status === 'ready' ||
    evaluatorsResult.status === 'reloading'
      ? evaluatorsResult.data
      : undefined;
  const evaluators = evaluatorsData
    ? ((evaluatorsData.evaluators ?? []) as ScorecardEvaluator[])
    : undefined;
  const applicantName = evaluatorsData?.applicant?.name;
  // Unknown until the list answers: Send waits for an explicit yes rather
  // than offering a mutation the server may refuse, and the permission
  // notice renders only on an explicit no.
  const canManage =
    evaluatorsData === undefined
      ? undefined
      : evaluatorsData.can_manage !== false;

  const viewTracked = useRef(false);
  useEffect(() => {
    if (!evaluators || viewTracked.current) return;
    // Once per open: a refetch after a removal settles back to ready, and
    // that is not another modal view.
    viewTracked.current = true;
    trackAnalyticsEvent('scorecards:invite_modal_view', {
      template_id: templateId,
      assigned_count: evaluators.length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluatorsResult.status]);

  const { result: searchResult } = useEvaluatorSearch(
    templateId,
    applicantId,
    searchQuery,
  );
  const searchActive = inputValue.trim().length >= SEARCH_MIN_LENGTH;
  const options =
    searchActive && searchResult.status === 'ready'
      ? ((searchResult.data.users ?? []) as EvaluatorCandidate[]).filter(
          user => !selected.some(chosen => chosen.id === user.id),
        )
      : [];

  const { result: inviteResult, mutation: sendInvitations } =
    useApiServiceMutation<
      { invited_count?: number }, // eslint-disable-line camelcase
      (
        templateId: number,
        applicantId: string,
        requestBody: { user_ids: string[] }, // eslint-disable-line camelcase
      ) => CancelablePromise<{ invited_count?: number }>, // eslint-disable-line camelcase
      { message?: string }
    >(
      ScoreCardEvaluatorsService.postInternalApiScoreCardsTemplatesApplicantsEvaluators,
      {
        onSuccess: data => {
          const count = data.invited_count ?? selected.length;
          setSentCount(count);
          trackAnalyticsEvent('scorecards:invite_modal_send', {
            template_id: templateId,
            evaluator_count: count,
          });
        },
      },
    );
  const sending = inviteResult.status === 'loading';

  const { result: removeResult, mutation: removeEvaluatorMutation } =
    useApiServiceMutation<
      RemoveEvaluatorResponse,
      (
        templateId: number,
        applicantId: string,
        userId: string,
      ) => CancelablePromise<RemoveEvaluatorResponse>,
      { message?: string }
    >(
      ScoreCardEvaluatorsService.deleteInternalApiScoreCardsTemplatesApplicantsEvaluators,
      { onSuccess: () => refetchEvaluators() },
    );

  const handleRemoveEvaluator = (userId: string) =>
    removeEvaluatorMutation(templateId, applicantId, userId);

  // Named so the confirmation says who was reminded; the list itself does
  // not change on a reminder.
  const [remindedName, setRemindedName] = useState<string | null>(null);
  const lastRemindedId = useRef<string | null>(null);

  const { result: remindResult, mutation: remindEvaluatorMutation } =
    useApiServiceMutation<
      void,
      (
        templateId: number,
        applicantId: string,
        userId: string,
      ) => CancelablePromise<void>,
      { message?: string }
    >(
      ScoreCardEvaluatorsService.postInternalApiScoreCardsTemplatesApplicantsEvaluatorsReminder,
      {
        onSuccess: () => {
          trackAnalyticsEvent('scorecards:evaluator_reminder_send', {
            template_id: templateId,
            evaluator_id: lastRemindedId.current,
          });
        },
      },
    );
  const remindPending = remindResult.status === 'loading';

  const handleRemindEvaluator = (userId: string) => {
    // One send at a time: the endpoint re-sends on every call by design, so
    // the guard against an impatient double click lives here, like `sending`
    // guards the invite below.
    if (remindPending) return;
    const evaluator = evaluators?.find(
      candidate => candidate.user.id === userId,
    );
    lastRemindedId.current = userId;
    setRemindedName(evaluator?.user.name ?? null);
    remindEvaluatorMutation(templateId, applicantId, userId);
  };

  useEffect(() => {
    if (sentCount === null) return undefined;
    const timer = setTimeout(handleClose, AUTO_CLOSE_DELAY);
    return () => clearTimeout(timer);
  }, [sentCount, handleClose]);

  const handleSend = () => {
    if (selected.length === 0 || sending || canManage !== true) return;
    sendInvitations(templateId, applicantId, {
      user_ids: selected.map(user => user.id), // eslint-disable-line camelcase
    });
  };

  const removeSelected = (id: string) =>
    setSelected(previous => previous.filter(user => user.id !== id));

  const assignedSection = () => {
    if (evaluatorsResult.isError) {
      return (
        <InlineMessage
          type="red"
          message={<FormattedMessage {...messages.evaluatorsLoadFailed} />}
        />
      );
    }
    if (!evaluators) {
      return (
        <div className={classes.loading}>
          <Loader block size="2rem" />
        </div>
      );
    }
    return (
      <AssignedEvaluators
        evaluators={evaluators}
        canManage={canManage === true}
        remindPending={remindPending}
        onRemind={handleRemindEvaluator}
        onRemove={handleRemoveEvaluator}
      />
    );
  };

  const form = (
    <div className={classes.body}>
      {applicantName && (
        <Typography variant="body2">
          <FormattedMessage
            {...messages.inviteIntro}
            values={{ applicantName, scorecardName }}
          />
        </Typography>
      )}
      {assignedSection()}
      {canManage === false && (
        <InlineMessage
          type="blue"
          message={<FormattedMessage {...messages.noManagePermission} />}
        />
      )}
      {removeResult.isError && (
        <InlineMessage
          type="red"
          message={<FormattedMessage {...messages.removeEvaluatorFailed} />}
        />
      )}
      {remindResult.status === 'ready' && remindedName && (
        <InlineMessage
          type="green"
          message={
            <FormattedMessage
              {...messages.reminderSent}
              values={{ name: remindedName }}
            />
          }
        />
      )}
      {remindResult.isError && (
        <InlineMessage
          type="red"
          message={<FormattedMessage {...messages.reminderFailed} />}
        />
      )}
      <Autocomplete
        multiple
        open={searchActive}
        options={options}
        loading={searchActive && searchResult.status === 'loading'}
        loadingText={<Loader size="1rem" />}
        value={selected}
        inputValue={inputValue}
        onInputChange={(_event, value, reason) => {
          if (reason !== 'input') return;
          setInputValue(value);
          debounceSearchQuery(value);
        }}
        onChange={(_event, value) => {
          setSelected(value as EvaluatorCandidate[]);
          setInputValue('');
          setSearchQuery('');
        }}
        getOptionLabel={user => user.name ?? user.email}
        getOptionSelected={(option, value) => option.id === value.id}
        filterOptions={x => x}
        renderTags={() => null}
        forcePopupIcon={false}
        disableClearable
        noOptionsText={
          searchResult.isError ? (
            <Typography variant="body2">
              <FormattedMessage {...messages.searchFailed} />
            </Typography>
          ) : (
            <div>
              <Typography variant="body2">
                <FormattedMessage {...messages.noUsersMatch} />
              </Typography>
              <Typography variant="caption" className={classes.optionEmail}>
                <FormattedMessage {...messages.onlyExistingUsers} />
              </Typography>
            </div>
          )
        }
        renderOption={user => (
          <div className={classes.option}>
            <Avatar className={classes.userAvatar}>
              {getInitials(user.name ?? user.email)}
            </Avatar>
            <div className={classes.optionIdentity}>
              <Typography variant="body2" className={classes.optionName}>
                {user.name}
              </Typography>
              <Typography variant="caption" className={classes.optionEmail}>
                {user.email}
              </Typography>
            </div>
          </div>
        )}
        renderInput={params => (
          <div ref={params.InputProps.ref}>
            <Input
              label={<FormattedMessage {...messages.evaluatorsLabel} />}
              placeholder={intl.formatMessage(messages.searchUsersPlaceholder)}
              inputProps={{
                ...params.inputProps,
                'data-testid': 'evaluator-search-input',
              }}
              fullWidth
            />
          </div>
        )}
      />
      {selected.length > 0 && (
        <div className={classes.chips} data-testid="selected-evaluators">
          {selected.map(user => (
            <Chip
              key={user.id}
              className={classes.chip}
              classes={{ avatar: classes.userAvatar, label: classes.chipLabel }}
              avatar={<Avatar>{getInitials(user.name ?? user.email)}</Avatar>}
              deleteIcon={<Close />}
              label={user.name ?? user.email}
              onDelete={() => removeSelected(user.id)}
              data-testid={`selected-evaluator-${user.id}`}
            />
          ))}
        </div>
      )}
      {inviteResult.isError && (
        <InlineMessage
          type="red"
          message={<FormattedMessage {...messages.inviteFailed} />}
        />
      )}
    </div>
  );

  const success = (
    <div className={classes.success} data-testid="invite-evaluators-success">
      <Success className={classes.successIcon} />
      <Typography variant="body1" className={classes.successTitle}>
        <FormattedMessage {...messages.invitationsSent} />
      </Typography>
      <Typography variant="body2" className={classes.successDetail}>
        <FormattedMessage
          {...messages.invitationsSentDetail}
          values={{ count: sentCount ?? 0 }}
        />
      </Typography>
    </div>
  );

  return (
    <Modal
      data-testid="invite-evaluators-modal"
      ariaLabelledBy="invite-evaluators-modal-header"
      open
      onClose={handleClose}
      fullScreenOnMobile
      maxWidth={640}
    >
      <ModalHeader
        onClose={handleClose}
        ariaLabelledBy="invite-evaluators-modal-header"
        showIcon={false}
      >
        <FormattedMessage {...messages.inviteEvaluators} />
      </ModalHeader>
      <ModalContent>{sentCount === null ? form : success}</ModalContent>
      {sentCount === null && (
        <ModalFooter>
          <Button
            autoWidth
            type="secondary"
            size="medium"
            onClick={handleClose}
            disabled={sending}
          >
            <FormattedMessage {...globalMessages.cancel} />
          </Button>
          <Button
            autoWidth
            type="primary"
            size="medium"
            onClick={handleSend}
            disabled={selected.length === 0 || sending || canManage !== true}
            isLoading={sending}
            data-testid="send-invitations-button"
          >
            <FormattedMessage
              {...(selected.length > 1
                ? messages.sendInvitations
                : messages.sendInvitation)}
            />
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
};
